type Child = Node | string | null | undefined | false;
type Attrs = Record<string, string | number | boolean | EventListener | undefined>;

/**
 * Tiny element builder: `el("button", { class: "btn", onclick: fn }, "Label")`.
 * `on*` keys become event listeners; `true` booleans become empty attributes.
 */
export function el<K extends keyof HTMLElementTagNameMap>(
	tag: K,
	attrs: Attrs = {},
	...children: Child[]
): HTMLElementTagNameMap[K] {
	const node = document.createElement(tag);
	for (const [key, value] of Object.entries(attrs)) {
		if (value === undefined || value === false) continue;
		if (key.startsWith("on") && typeof value === "function") {
			node.addEventListener(key.slice(2), value);
		} else if (value === true) {
			node.setAttribute(key, "");
		} else {
			node.setAttribute(key, String(value));
		}
	}
	for (const child of children) {
		if (child === null || child === undefined || child === false) continue;
		node.append(child);
	}
	return node;
}

/** True when a key event is aimed at a text field, so game shortcuts must not react. */
export function isTypingTarget(ev: Event): boolean {
	const t = ev.target;
	return (
		t instanceof HTMLTextAreaElement ||
		(t instanceof HTMLInputElement && (t.type === "text" || t.type === "search")) ||
		(t instanceof HTMLElement && t.isContentEditable)
	);
}
